
import React, { useEffect, useRef } from 'react';
import { Typography, Box } from '@mui/material';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BuildIcon from '@mui/icons-material/Build';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import './Hero.css';
import logo from '../images/LogoNoDrill.png';
import logoSparks from '../images/LogoSparks.png';
import logoText from '../images/LogoText.png';
import heroDrill from '../images/heroDrill.png';
import sparksVideo from '../videos/sparks.mp4';
import image1 from '../images/SampleImage(1).png';
import image2 from '../images/SampleImage(2).png';
import image3 from '../images/SampleImage(3).png';
import image5 from '../images/SampleImage(5).png';
import { useSiteContent } from '../context/SiteContentContext';
import Button from '@mui/material/Button';
import { COLORS } from '../theme/colors';

const heroIconSx = (theme) => ({
    fontSize: {xs: '2rem', sm: '2.25rem'},
    [theme.breakpoints.up(1130)]: {
        fontSize: '3.5rem',
    },
});

function renderHeroHeadline(headline) {
    const highlight = 'CNC Shop';
    const idx = headline.toLowerCase().indexOf(highlight.toLowerCase());
    if (idx === -1) {
        return <span style={{ color: COLORS.mutedText }}>{headline}</span>;
    }

    const before = headline.slice(0, idx);
    const match = headline.slice(idx, idx + highlight.length);
    const after = headline.slice(idx + highlight.length);

    return (
        <>
            <span style={{ color: COLORS.mutedText }}>{before}</span>
            <span style={{ color: '#000000', fontWeight: 900 }}>{match}</span>
            <span style={{ color: COLORS.mutedText }}>{after}</span>
        </>
    );
}

const heroHighlights = [
    {
        icon: <PrecisionManufacturingIcon sx={heroIconSx} />,
        title: 'Precision Machining',
        description: 'Tight tolerances down to ±.001 in on every part',
    },
    {
        icon: <ScheduleIcon sx={heroIconSx} />,
        title: 'Fast Turnaround',
        description: 'Lead times starting from 5 business days',
    },
    {
        icon: <BuildIcon sx={heroIconSx} />,
        title: 'Full-Service Shop',
        description: 'Prototyping, production, post-processing & delivery',
    },
    {
        icon: <WorkspacePremiumIcon sx={heroIconSx} />,
        title: '25+ Years Experience',
        description: 'Trusted precision machining since day one',
    },
];

function Hero({ headline }) {
    const { heroHeadline: contextHeadline } = useSiteContent();
    const heroHeadline = headline ?? contextHeadline;
    const sparksRef = useRef(null);

    useEffect(() => {
        const video = sparksRef.current;
        if (!video) return;

        let playCount = 0;
        const maxPlays = 1;
        video.playbackRate = 2;

        const handleEnded = () => {
            playCount += 1;
            if (playCount < maxPlays) {
                video.currentTime = 0;
                video.play();
            }
        };
        video.addEventListener('ended', handleEnded);

        const timer = setTimeout(() => {
            video.play();
        }, 3000); // matches the drill's 3s drop animation

        return () => {
            clearTimeout(timer);
            video.removeEventListener('ended', handleEnded);
        };
    }, []);

      const handleScroll = (id) => {
        const target = document.getElementById(id);
        if (target) {
            target.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <Box className={`hero`} >
            <img className="bg-parts-left" src={image5} alt="" aria-hidden="true" />
            <img className="bg-parts-left-bottom" src={image1} alt="" aria-hidden="true" />
            <img className="bg-parts-right-bottom" src={image2} alt="" aria-hidden="true" />
            <img className="bg-parts-right" src={image3} alt="" aria-hidden="true" />
            <div className="hero-stage">
                <div className="hero-drill-wrapper">
                    <img
                        className="hero-drill-drop"
                        src={heroDrill}
                        alt="Drill"
                    />
                    <span className="drill-glint" aria-hidden="true" />
                </div>
                <video
                    ref={sparksRef}
                    className="hero-sparks-video"
                    src={sparksVideo}
                    muted
                    playsInline
                    aria-hidden="true"
                />
                <img
                    className="hero-logo"
                    src={logo}
                    alt="Alpha Tools & Components logo"
                />
                <img
                    className="hero-logo-sparks"
                    src={logoSparks}
                    alt=""
                    aria-hidden="true"
                />
                <img
                    className="company-name-fade"
                    src={logoText}
                    alt="Alpha Tools and Components Inc."
                />
                <Box className="hero-headline-wrap" sx={(theme) => ({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 10, [theme.breakpoints.up(1130)]: { mt: 4 } })}>
                    <Typography

                        sx={{

                            fontWeight: 'bold',
                            justifySelf: 'center',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'center',
                            fontSize: 'clamp(1.0rem, 4vw, 2.5rem)',
                        }}
                    >
                        {renderHeroHeadline(heroHeadline)}
                    </Typography>
                           <Button
                        className="Action-button"
                        variant='contained'
                        sx={{
                            borderRadius: '8px',
                            border: `2px solid ${COLORS.brandOrange}`,
                            fontSize: 'clamp(0.85rem, 2vw, 1.1rem)',
                            padding: 'clamp(6px, 1vw, 8px) clamp(16px, 3vw, 22px)',
                            background: COLORS.brandOrange,
                            boxShadow: `0`,
                            '&:hover': {
                                background: '#000',
                                borderColor: '#000',
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${COLORS.brandOrange}`,
                                outlineOffset: '2px',
                            },
                        }}
                        onClick={() => handleScroll('contact')}
                    >
                        Contact us
                        </Button>
                </Box>
                
            
            </div>
            <Box
                sx={(theme) => ({
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    mx: 'auto',
                    width: 'calc(100% - 48px)',
                    maxWidth: 650,
                    zIndex: 1,
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    bottom: 16,
                    gap: 3,

                    [theme.breakpoints.down(650)]: {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gridTemplateRows: 'repeat(2, 1fr)',
                        gap: 2,
                        maxWidth: 'none',
                    },
                    [theme.breakpoints.up(1130)]: {
                        gap: 6,
                        maxWidth: 'none',
                    },
                })}
            >
                {heroHighlights.map(({ icon, title, description }) => (
                    <Box
                        key={title}
                        sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            width: 'calc(50% - 12px)',
                            [theme.breakpoints.down(650)]: {
                                width: '100%',
                                flexDirection: 'column',
                                alignItems: 'center',
                            },
                            [theme.breakpoints.down(565)]: {
                               gap: 1,
                            },
                            [theme.breakpoints.up(1130)]: {
                                width: 220,
                            },
                        })}
                    >
                        <Box sx={(theme) => ({ color: COLORS.mutedText, mt: 0.5, display: 'flex', [theme.breakpoints.down(650)]: { mt: 0 } })}>{icon}</Box>
                        <Box sx={(theme) => ({ textAlign: 'left', flex: 1, minWidth: 0, [theme.breakpoints.down(650)]: { textAlign: 'center' } })}>
                            <Typography component="span" 
                            sx={(theme) => ({
                            display: 'block', 
                            fontWeight: 600, 
                            color: COLORS.mutedText, 
                            fontSize: '0.75rem',
                             })}>
                                {title}
                            </Typography>
                            <Typography
                                component="span"
                                variant="body2"
                                sx={(theme) => ({
                                    display: 'block',
                                    color: COLORS.mutedText,
                                    fontSize: '0.65rem',
                                    [theme.breakpoints.down(650)]: {
                                        display: 'none',
                                    },
                                })}
                            >
                                {description}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default Hero;
